/**
 * Captures the current tab the way @sentry/feedback does in
 * `useTakeScreenshot`: `getDisplayMedia({ preferCurrentTab: true })`, then
 * `drawImage` of that video frame onto a canvas. A live tab capture includes
 * the planning canvas bitmap. An extracted WebGL buffer does not, because
 * compositing has already cleared it.
 *
 * The video element has to stay in the document. Chrome rejects `play()` with
 * AbortError ("The play() request was interrupted because the media was
 * removed from the document") when it is detached, and that happens after the
 * user has already granted the tab. The granted stream then never becomes a
 * picture.
 */
export async function captureScreen(root: HTMLElement): Promise<Uint8Array> {
  const panels = [...root.querySelectorAll<HTMLElement>('[data-problem-report="panel"]')];
  const previousVisibility = panels.map((panel) => panel.style.visibility);
  for (const panel of panels) {
    panel.style.visibility = 'hidden';
  }

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.style.cssText =
    'position:fixed;left:-10000px;top:0;width:320px;height:180px;opacity:0;pointer-events:none';

  let stream: MediaStream | null = null;
  try {
    const dpi = window.devicePixelRatio || 1;
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: window.innerWidth * dpi,
        height: window.innerHeight * dpi,
      },
      audio: false,
      monitorTypeSurfaces: 'exclude',
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
    } as DisplayMediaStreamOptions);

    root.append(video);
    video.srcObject = stream;
    try {
      await video.play();
      await nextPresentedFrame(video);
    } catch {
      // Chrome aborts play() for a detached element after metadata is ready.
    }
    if (!video.videoWidth || !video.videoHeight) {
      await waitForDimensions(video);
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context || !canvas.width || !canvas.height) {
      throw new Error('The tab capture produced no image.');
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await blobFrom(canvas);
    if (!blob) {
      throw new Error('The tab capture produced no image.');
    }
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
    video.remove();
    panels.forEach((panel, index) => {
      panel.style.visibility = previousVisibility[index] ?? '';
    });
  }
}

/** Waits for the next painted frame, but does not block the shot if none arrives. */
function nextPresentedFrame(video: HTMLVideoElement): Promise<void> {
  const requestFrame = video.requestVideoFrameCallback?.bind(video);
  if (!requestFrame) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, 500);
    requestFrame(() => {
      window.clearTimeout(timeout);
      resolve();
    });
  });
}

function waitForDimensions(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth && video.videoHeight) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('The tab capture produced no image.'));
    }, 3000);
    const onReady = () => {
      if (!video.videoWidth || !video.videoHeight) {
        return;
      }
      cleanup();
      resolve();
    };
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('loadeddata', onReady);
    };
    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('loadeddata', onReady);
  });
}

function blobFrom(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error('The tab capture produced no image.'));
    }, 3000);
    canvas.toBlob((result) => {
      window.clearTimeout(timeout);
      resolve(result);
    }, 'image/png');
  });
}
